<script lang="ts" setup>

import { reactive, computed, ref, watch } from 'vue'
import type { CookieRef } from 'nuxt/dist/app/composables/cookie'
import RoomieButton from '@lahaus-roomie/roomie/src/components/RoomieButton/index.vue'
import RoomieInput, { STATUS as INPUT_STATUS } from '@lahaus-roomie/roomie/src/components/RoomieInput/index.vue'
import RoomiePhoneInput from '@lahaus-roomie/roomie/src/components/RoomiePhoneInput/index.vue'
import RightIcon from '@lahaus-roomie/roomie/src/assets/icons/right.svg'

import type { Props, PhoneProperties } from './types'
import { validateEmail } from '@/utils/validations/emailValidation'
import { LISTING_SOURCE, TYPE_ATTENTION } from '@/utils/segment'
import type { LeadCreationRequestedTraits } from '@/utils/segment/types'
import { useContactFormStore } from '@/stores/contactFormStore'
import LegalPolicies from '@/components/LegalPolicies/index.vue'

const { t, locale } = useI18n()

const emit = defineEmits(['submit', 'skipConfirmation'])

const legalPoliciesAccepted = ref(false)
const showWarningLegalPolicy = ref(false)

const opportunityTypeEventMap: Record<string, string> = {
  living: 'LIVING',
  investing: 'INVESTMENT'
}

const props = withDefaults(defineProps<Props>(), {
  listingIds: () => [],
  listingId: '',
  screen: '',
  screenCta: '',
  totalSavings: 0,
  showEmailInput: true,
  showNameInput: true,
  showCtaIcon: false,
  trackingCookieName: '',
  trackingCompletedMessage: '',
  saveCookie: true,
  showLegalPoliciesCheckbox: false,
  image: '',
  attentionType: TYPE_ATTENTION.COMMERCIAL_OPERATIONS
})

const contactFormStore = useContactFormStore()
const form = reactive({
  name: '',
  email: '',
  phone: contactFormStore.userPhone,
  phone_number: '',
  phone_prefix: '',
  phone_is_valid: false
})

const onboardingOpportunityType = useCookie<string>('onboarding_opportunity_type')
const onboardingBedroomsNumber = useCookie<number>('onboarding_bedrooms_number')

watch(() => contactFormStore.userPhone, () => {
  form.phone = contactFormStore.userPhone
})

const i18nStrings = computed(() => {
  return {
    great: t('investment_simulator.form_confirmation_message.great'),
    sentData: t('investment_simulator.form_confirmation_message.sent_data')
  }
})

let trackingCookie: CookieRef<string>

if (props.saveCookie) {
  trackingCookie = useCookie<string>(props.trackingCookieName)
}

const trackingCompleted = computed(() => {
  if (!props.saveCookie) return false

  return trackingCookie.value === 'completed'
})

const emailValidated = ref(false)

const emailStatus = computed(() => {
  if (!emailValidated.value) return INPUT_STATUS.default

  return validateEmail(form.email) ? INPUT_STATUS.success : INPUT_STATUS.error
})

const emailHelperText = computed(() => {
  if (emailStatus.value === INPUT_STATUS.success) return ''

  if (emailStatus.value === INPUT_STATUS.error) return form.email ? t('invalidEmail') : t('requiredEmail')

  return ''
})

const handleFormSubmit = async () => {
  emailValidated.value = true

  if (!legalPoliciesAccepted.value && !contactFormStore.legalConsent) {
    showWarningLegalPolicy.value = true
    return
  }

  showWarningLegalPolicy.value = false

  if (props.showEmailInput && emailStatus.value === INPUT_STATUS.error) return

  const isPhoneValid = form.phone_is_valid

  if (isPhoneValid) {
    emit('submit', form)
    contactFormStore.updateUserPhone(form.phone)
    await trackLeadCreationRequest()
    updateTrackingCookie()
  }

  if (props.skipConfirmation) {
    emit('skipConfirmation')
  }
}

const { $trackLead } = useNuxtApp()

const trackLeadCreationRequest = async () => {
  const leadPayload = getLCRProperties()
  await $trackLead({
    phone: leadPayload.phone,
    listings: [
      leadPayload.listing_id
    ],
    screen: leadPayload.screen,
    screenCta: leadPayload.screen_cta,
    properties: leadPayload
  })

  // @ts-ignore
  window.dataLayer?.push({
    event: 'lead-creation-attempted',
    screen: leadPayload.screen,
    screen_cta: leadPayload.screen_cta
  })

  // @ts-ignore
  window.dataLayer?.push({
    event: 'lead-creation-requested',
    phone: leadPayload.phone,
    screen: leadPayload.screen,
    screen_cta: leadPayload.screen_cta
  })
}

const updateTrackingCookie = () => {
  if (!props.saveCookie) return
  trackingCookie.value = 'completed'
}

const getLCRProperties = () => {
  const data = {
    phone: form.phone,
    phone_number: form.phone_number,
    phone_prefix: form.phone_prefix,
    screen: props.screen,
    screen_cta: props.screenCta,
    location_of_interest_codes: [locale.value.toUpperCase()],
    budget_currency: t('currency'),
    type_attention: props.attentionType,
    purchase_purpose: onboardingOpportunityType.value && opportunityTypeEventMap[onboardingOpportunityType.value] ? opportunityTypeEventMap[onboardingOpportunityType.value] : 'INVESTMENT_AND_LIVING'
  } as LeadCreationRequestedTraits

  if (form.name) {
    data.first_name = form.name.split(' ')[0]
    data.last_name = form.name.split(' ')[1] || ''
    data.full_name = form.name
  }

  if (form.email) {
    data.email = form.email.toLowerCase()
  }

  if (props.listingId) {
    data.listing_id = props.listingId
    data.listing_source = LISTING_SOURCE.PROPERTY_CATALOG
  }

  if (props.totalSavings) {
    data.down_payment_amount = props.totalSavings
  }

  if (props.monthlyPayments !== null) {
    data.monthly_payment_budget_max = props.monthlyPayments
  }

  if (onboardingBedroomsNumber.value) {
    data.bedrooms = onboardingBedroomsNumber.value
  }

  return data
}

const getPhoneProperties = (value:PhoneProperties) => {
  form.phone_number = value.phoneNumber
  form.phone_prefix = value.code
}

const roomieButtonDisabled = computed(() => {
  if (!showWarningLegalPolicy.value) return false
  return !legalPoliciesAccepted.value && !contactFormStore.legalConsent
})

</script>

<script lang="ts">
export default {
  name: 'ContactForm'
}
</script>

<template>
  <div
    v-if="!trackingCompleted"
    class="contact-form"
    :class="{ 'dark-mode': darkMode }">
    <picture>
      <img
        v-if="image"
        id="contactFormImage"
        :src="image"
        alt="ícono La Haus"
        style="width: 64px; height: 64px;">
    </picture>

    <h2
      v-if="title"
      class="text-18 font-medium my-8"
      :class="{ 'text-white': darkMode }">
      {{ title }}
    </h2>

    <slot name="description" />

    <form
      class="mt-24 flex flex-col gap-y-16"
      @submit.prevent="handleFormSubmit">
      <RoomieInput
        v-if="showNameInput"
        id="contact-form-name"
        v-model="form.name"
        :required="true"
        label="Nombre"
        :is-internal-validate="false" />

      <RoomieInput
        v-if="showEmailInput"
        id="contact-form-email"
        v-model="form.email"
        :required="true"
        label="Correo"
        :status="emailStatus"
        :is-internal-validate="false"
        :helper-text="emailHelperText"
        @blur="emailValidated = true" />

      <RoomiePhoneInput
        id="lead-form-phone"
        name="lead-form-phone"
        :initial-country="locale"
        number-label="Número*"
        country-label="País"
        :is-required="true"
        :value="form.phone"
        @input="(newValue: string) => form.phone = newValue"
        @is-valid="(isValid: boolean) => form.phone_is_valid = isValid"
        @full="(value: PhoneProperties) => getPhoneProperties(value)" />

      <slot name="extraInfo" />

      <LegalPolicies
        v-if="!contactFormStore.legalConsent"
        v-model="legalPoliciesAccepted"
        :phone="form.phone"
        :show-warning="showWarningLegalPolicy"
        :screen="screen"
        :screen-cta="screenCta" />

      <RoomieButton
        :id="`${id}-submit-button`"
        class="w-full gap-8"
        :data-lh-id="`${id}-submit-button`"
        type="submit"
        :icon-placement="iconPlacement"
        :aria-label="ctaCopie"
        :disabled="roomieButtonDisabled">
        <template #icon>
          <span>
            <NuxtIsland :name="iconName" />
          </span>
        </template>

        <span class="flex">
          {{ ctaCopie }}
          <RightIcon
            v-if="showCtaIcon"
            class="h-24 w-24 fill-white ml-4" />
        </span>
      </RoomieButton>
    </form>
  </div>

  <div
    v-else-if="!skipConfirmation"
    class="mt-24 py-12">
    <div class="flex items-center">
      <div class="w-32 h-32 flex items-center flex-none rounded-circle bg-primary-50">
        <span class="w-32 h-32 fill-brand-800 flex justify-center pointer-events-none">
          <NuxtIsland name="IconCheckSm" />
        </span>
      </div>

      <div class="ml-12">
        <p class="text-18 font-medium">
          {{ i18nStrings.great }}
        </p>

        <p
          class="text-14">
          {{ i18nStrings.sentData }}
        </p>
      </div>
    </div>

    <div class="flex mt-32">
      <div class="w-32 h-32 flex items-center justify-center flex-none rounded-circle bg-primary-50">
        <span class="w-20 h-20 fill-carbon-800 flex justify-center pointer-events-none">
          <NuxtIsland name="IconEmailSend" />
        </span>
      </div>

      <p class="text-14 ml-12">
        {{ trackingCompletedMessage }}
      </p>
    </div>
  </div>
</template>

<style lang="scss">
.contact-form {
  @apply mt-32;

  & .roomie-button__content {
    @apply flex;
  }

  &.dark-mode {
    .roomie-phone-input__label,
    .roomie-phone-input--success .roomie-phone-input__helper-text,
    .roomie-phone-input--error .roomie-phone-input__helper-text {
      @apply text-white;
    }

    .roomie-phone-input .iti--separate-dial-code .iti__selected-flag {
      @apply bg-white;
    }
  }
}
</style>

<i18n src="./i18n.json" lang="json"></i18n>
